import React, {useContext, useEffect, useState} from 'react';
import {
    Typography,
    Button,
    Box,
    Grid,
    Paper,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Slide,
    Zoom,
    Fade
} from '@mui/material';
import { ShoppingCartCheckout, RemoveShoppingCart, LocalMall } from '@mui/icons-material';
import '../../styles/appStyles.css';
import {Delete} from '@mui/icons-material';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import AppContext from '../../AppContext';

const Cart = () => {
    const {cart, setCart} = useContext(AppContext);
    const navigate = useNavigate();
    const [isDialogOpen, setDialogOpen] = useState(false);
    const {code, setCode} = useContext(AppContext);
    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

    // Stan dla uwag do zamówienia
    const [orderComment, setOrderComment] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!cart || cart.length === 0) {
        return <Navigate to="/" />;
    }

    const updateQuantity = (productId, delta) => {
        const updatedCart = cart.map((item) =>
            item.id === productId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
        );
        setCart(updatedCart);
    };

    const removeItem = (productId) => {
        const updatedCart = cart.filter((item) => item.id !== productId);
        setCart(updatedCart);
    };

    const clearCart = () => {
        setCart([]);
        setDialogOpen(false);
    };

    const confirmOrder = async () => {
        const payload = {
            items: cart.map((order) => ({
                id: order.id,
                quantity: order.quantity,
            })),
            comment: orderComment,
        };

        try {
            const encodedCode = encodeURIComponent(code);
            const response = await fetch(`${baseUrl}/api/customers/setOrder?code=${encodedCode}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to confirm order');
            }
            setCart([]);
            navigate('/');
        } catch (error) {
        }
    };

    const totalPrice = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return (
        <Box>
            <Fade in={true} timeout={800}>
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: 3, 
                        mb: 2,
                        borderRadius: '15px',
                        background: 'linear-gradient(135deg, #fff, #f9f9f9)' 
                    }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography 
                            variant="h4" 
                            sx={{
                                fontWeight: 'bold',
                                backgroundImage: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <LocalMall sx={{ mr: 1, fontSize: '2rem' }} />
                            Twój koszyk
                        </Typography>
                        {cart.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setDialogOpen(true)}
                                startIcon={<RemoveShoppingCart />}
                                sx={{ 
                                    borderRadius: '50px', 
                                    borderWidth: '2px',
                                    px: 2,
                                    fontWeight: 'bold',
                                    '&:hover': { borderWidth: '2px', backgroundColor: 'rgba(255,71,87,0.05)' }
                                }}
                            >
                                Wyczyść koszyk
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Fade>
            {cart.length === 0 ? (
                <Typography 
                    variant="h5" 
                    sx={{ 
                        textAlign: 'center', 
                        py: 5, 
                        color: 'var(--dark-color)',
                        opacity: 0.7 
                    }}
                >
                    Twój koszyk jest pusty. 🛒
                </Typography>
            ) : (
                <>
                    <Grid container>
                        {cart.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                                    <Paper className="cart-item-card">
                                        {item.image && (
                                            <Box>
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="cart-image"
                                                />
                                            </Box>
                                        )}
                                        <Box className="cart-item-info">
                                            <Typography className="cart-item-title">{item.name}</Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: 'var(--primary-color)', 
                                                    fontWeight: 600 
                                                }}
                                            >
                                                {item.price} zł
                                            </Typography>
                                            <Box className="cart-quantity-controls">
                                                <Typography sx={{ color: 'var(--dark-color)', mr: 1 }}>Ilość:</Typography>
                                                <button 
                                                    className="quantity-btn"
                                                    onClick={() => updateQuantity(item.id, -1)}
                                                >
                                                    -
                                                </button>
                                                <span className="quantity-value">{item.quantity}</span>
                                                <button 
                                                    className="quantity-btn"
                                                    onClick={() => updateQuantity(item.id, 1)}
                                                >
                                                    +
                                                </button>
                                            </Box>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    color: 'var(--primary-color)',
                                                    mt: 1 
                                                }}
                                            >
                                                Suma: {(item.price * item.quantity).toFixed(2)} zł
                                            </Typography>
                                        </Box>
                                        <Box ml="auto">
                                            <IconButton
                                                onClick={() => removeItem(item.id)}
                                                sx={{
                                                    color: 'white',
                                                    backgroundColor: 'var(--error-color)',
                                                    '&:hover': {
                                                        backgroundColor: 'var(--error-color)',
                                                        filter: 'brightness(1.1)'
                                                    },
                                                    width: '40px',
                                                    height: '40px'
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>

                    <Paper 
                        elevation={3} 
                        sx={{ 
                            mt: 2,
                            p: 3, 
                            borderRadius: '15px',
                            background: 'linear-gradient(135deg, #fff, #f9f9f9)' 
                        }}>
                        <Typography 
                            variant="h5" 
                            sx={{ 
                                textAlign: 'center',
                                mb: 2,
                                fontWeight: 'bold',
                                color: 'var(--dark-color)' 
                            }}
                        >
                            Podsumowanie
                        </Typography>

                        <Typography 
                            variant="h4" 
                            sx={{ 
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: 'var(--primary-color)',
                                mb: 3
                            }}
                        >
                            {totalPrice.toFixed(2)} zł
                        </Typography>

                        <Box mt={2} sx={{ background: 'rgba(0,0,0,0.03)', p: 2, borderRadius: '10px' }}>
                            <TextField
                                label="Uwagi do zamówienia"
                                multiline
                                rows={3}
                                value={orderComment}
                                onChange={(e) => setOrderComment(e.target.value)}
                                fullWidth
                                variant="outlined"
                                inputProps={{ maxLength: 1000 }}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        backgroundColor: 'white'
                                    }
                                }}
                            />
                        </Box>
                    </Paper>
                </>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2, mt: 2 }}>
                {cart.length > 0 && (
                    <Zoom in={true} timeout={500}>
                        <Button 
                            className="action-button primary-button"
                            onClick={confirmOrder}
                            startIcon={<ShoppingCartCheckout />}
                            sx={{ minWidth: '200px', py: 1.5, color: 'white !important', textShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)' }}
                        >
                            Złóż zamówienie
                        </Button>
                    </Zoom>
                )}

                <Zoom in={true} timeout={500} style={{ transitionDelay: cart.length > 0 ? '100ms' : '0ms' }}>
                    <Button 
                        component={Link} 
                        to="/menu" 
                        variant="outlined"
                        sx={{ 
                            borderRadius: '50px',
                            borderWidth: '2px',
                            py: 1.5,
                            px: 3,
                            minWidth: '200px',
                            borderColor: 'var(--primary-color)',
                            color: 'var(--primary-color)',
                            '&:hover': { borderWidth: '2px' }
                        }}
                    >
                        Wróć do menu
                    </Button>
                </Zoom>
            </Box>
            <Dialog 
                open={isDialogOpen} 
                onClose={() => setDialogOpen(false)}
                PaperProps={{ 
                    className: "custom-dialog",
                    sx: { borderRadius: '20px', overflow: 'hidden' }
                }}
            >
                <DialogTitle className="dialog-title">Potwierdź akcję</DialogTitle>
                <DialogContent className="dialog-content">
                    <DialogContentText sx={{ color: 'var(--dark-color)', opacity: 0.8 }}>
                        Czy na pewno chcesz wyczyścić cały koszyk? Ta akcja nie może zostać cofnięta.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="dialog-actions">
                    <Button 
                        onClick={() => setDialogOpen(false)}
                        sx={{ 
                            borderRadius: '50px', 
                            px: 3,
                            color: 'var(--dark-color)',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } 
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={clearCart} 
                        className="action-button error-button"
                        startIcon={<RemoveShoppingCart />}
                        sx={{ borderRadius: '50px', px: 3 }}
                    >
                        Wyczyść koszyk
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Cart;